/**
 * The masthead marks, ported from the generator's `themeMarks` object.
 *
 * Chosen by `Meta.ThemeMark`, defaulting to Skyline exactly as the generator
 * does. Geometry, viewBox, stroke colour and widths are copied unchanged — this
 * is decoration that must match the reference pixel for pixel, so nothing here
 * has been tidied, including the literal #52514e that does not go through a
 * custom property in the original either.
 */
import type { ReactElement } from 'react';

const STROKE = '#52514e';

function Skyline() {
  return (
    <svg className="theme-mark" width="80" height="52" viewBox="0 0 86 56" fill="none" aria-hidden="true">
      <rect x="6" y="18" width="14" height="34" stroke={STROKE} strokeWidth="1" />
      <rect x="24" y="8" width="16" height="44" stroke={STROKE} strokeWidth="1" />
      <rect x="44" y="24" width="12" height="28" stroke={STROKE} strokeWidth="1" />
      <rect x="60" y="14" width="18" height="38" stroke={STROKE} strokeWidth="1" />
      <line x1="0" y1="52" x2="86" y2="52" stroke={STROKE} strokeWidth="1" />
    </svg>
  );
}

function Building() {
  return (
    <svg className="theme-mark" width="70" height="52" viewBox="0 0 60 56" fill="none" aria-hidden="true">
      <rect x="10" y="6" width="40" height="46" stroke={STROKE} strokeWidth="1" />
      <line x1="10" y1="18" x2="50" y2="18" stroke={STROKE} strokeWidth="1" />
      <line x1="10" y1="30" x2="50" y2="30" stroke={STROKE} strokeWidth="1" />
      <line x1="10" y1="42" x2="50" y2="42" stroke={STROKE} strokeWidth="1" />
      <line x1="24" y1="6" x2="24" y2="52" stroke={STROKE} strokeWidth="1" />
      <line x1="36" y1="6" x2="36" y2="52" stroke={STROKE} strokeWidth="1" />
      <polygon
        points="6,6 30,-2 54,6"
        stroke={STROKE}
        strokeWidth="1"
        fill="none"
        transform="translate(0,4)"
      />
    </svg>
  );
}

function Abstract() {
  return (
    <svg className="theme-mark" width="70" height="52" viewBox="0 0 60 56" fill="none" aria-hidden="true">
      <circle cx="30" cy="28" r="22" stroke={STROKE} strokeWidth="1" />
      <line x1="30" y1="6" x2="30" y2="50" stroke={STROKE} strokeWidth="1" />
      <line x1="8" y1="28" x2="52" y2="28" stroke={STROKE} strokeWidth="1" />
      <polygon points="30,14 36,28 30,42 24,28" stroke={STROKE} strokeWidth="1" fill="none" />
    </svg>
  );
}

const MARKS: Record<string, () => ReactElement> = { Skyline, Building, Abstract };

/** `Meta.ThemeMark` is a free-text cell, so an unknown value falls back rather than throwing. */
export function ThemeMark({ name }: { name?: unknown }) {
  const Mark = (typeof name === 'string' ? MARKS[name] : undefined) ?? Skyline;
  return <Mark />;
}
